import { FormEvent, lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { AppIcon } from './AppIcon';
import type { AppIconName } from './AppIcon';
import { itinerary } from './data/itinerary';
import { discoverCategoryLabels, recommendationsForDay } from './data/discover';
import type { DiscoverCategory } from './data/discover';
import { ticketMeta, ticketMetaByEvent } from './data/tickets';
import { applyPlanningState, emptyPlanningState, loadPlanningState, savePlanningState } from './planning';
import type { PlanningState } from './planning';
import { clearWalletKey, deleteExpense, getExpenseOwner, getWalletKey, listExpenses, saveExpense, setExpenseOwner, setWalletKey } from './storage';
import { decryptWalletWithPassphrase, decryptWalletWithStoredKey } from './wallet';
import type { Expense, ExpenseCurrency, ExpenseOwner, RouteRequest, TravelerId, TripDay, TripEvent, WalletPayload, WalletTicket } from './types';
import { APP_VERSION } from './version';
import { offlineCityPacks } from './data/offlinePacks';

const CityMap = lazy(() => import('./CityMap'));

const tabs = [
  ['today', 'Hoy', 'today'],
  ['trip', 'Viaje', 'trip'],
  ['tickets', 'Boletos', 'tickets'],
  ['maps', 'Mapas', 'maps'],
  ['more', 'Más', 'more'],
] as const satisfies ReadonlyArray<readonly [string, string, AppIconName]>;

type Tab = (typeof tabs)[number][0];
type MoreSection = 'menu' | 'discover' | 'expenses';

const categories = [
  ['food', 'food', 'Comida'],
  ['transport', 'taxi', 'Traslados'],
  ['clothes', 'shirt', 'Ropa'],
  ['souvenirs', 'gift', 'Recuerdos'],
  ['alcohol', 'wine', 'Alcohol'],
  ['tickets', 'tickets', 'Entradas'],
  ['market', 'shoppingCart', 'Supermercado'],
  ['coffee', 'coffee', 'Café'],
  ['pharmacy', 'pill', 'Farmacia'],
  ['lodging', 'hotel', 'Hospedaje'],
  ['tips', 'banknote', 'Propinas'],
  ['other', 'plus', 'Otro'],
] as const satisfies ReadonlyArray<readonly [string, AppIconName, string]>;

const ownerGroups: Record<ExpenseOwner, TravelerId[]> = {
  ricardo: ['ricardo', 'aurora'], arturo: ['arturo', 'ileana', 'arturo-jr'],
};

function dateParts(value: string) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function labelDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(dateParts(value));
}

function longDate(value: string) {
  return new Intl.DateTimeFormat('es-MX', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(dateParts(value));
}

function localIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function operationalDay(days: TripDay[]): TripDay {
  const today = localIsoDate();
  return days.find((day) => day.date === today) ?? days.find((day) => day.date > today) ?? days[days.length - 1];
}

function eventVisual(event: TripEvent): { label: string; icon: AppIconName } {
  if (event.kind === 'flight') return { label: 'Vuelo', icon: 'flight' };
  if (event.kind === 'train') return { label: 'Tren', icon: 'train' };
  if (event.kind === 'hotel') return { label: 'Alojamiento', icon: 'hotel' };
  if (event.kind === 'visit') return { label: 'Reservado', icon: 'reserved' };

  const context = `${event.title} ${event.subtitle ?? ''}`.toLocaleLowerCase('es');
  if (context.includes('taxi')) return { label: 'Taxi', icon: 'taxi' };
  if (context.includes('metro')) return { label: 'Metro', icon: 'metro' };
  if (context.includes('suv') || context.includes('rentad') || context.includes('devolver coche') || context.includes('recoger coche')) return { label: 'Rentado', icon: 'rental' };
  if (context.includes('traslado') || context.includes('→') || context.includes('aeropuerto')) return { label: 'Cómo llegar', icon: 'directions' };
  return { label: 'Plan', icon: 'plan' };
}

function PassengerDetails({ event }: { event: TripEvent }) {
  if (!event.passengers?.length) return null;
  return (
    <div className="passenger-block">
      <div className="passenger-block-head"><strong>Pasajeros</strong><span>{event.passengers.length}</span></div>
      <div className="passenger-list">
        {event.passengers.map((passenger) => {
          const position = [passenger.coach ? `Coche ${passenger.coach}` : '', passenger.seat ? `Asiento ${passenger.seat}` : ''].filter(Boolean).join(' · ');
          return (
            <div className="passenger-row" key={`${event.id}-${passenger.travelerId}`}>
              <div><strong>{passenger.name}</strong>{passenger.booking && <small>Localizador {passenger.booking}</small>}</div>
              <div className="passenger-meta">{position && <b>{position}</b>}{passenger.baggage && <small>{passenger.baggage}</small>}{passenger.note && <small>{passenger.note}</small>}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventDetails({ event, onOpenTicket, onRoute, onEdit }: { event: TripEvent; onOpenTicket: (eventId: string) => void; onRoute: (route: RouteRequest) => void; onEdit: (eventId: string) => void }) {
  const visual = eventVisual(event);
  return (
    <article className={`detail-event ${event.status === 'review' ? 'review' : ''}`}>
      <div className="detail-event-head">
        <div className="event-clock"><span>{event.time ?? '—'}</span><small><AppIcon name={visual.icon} size={14} /><span>{visual.label}</span></small></div>
        <div className="detail-event-main">
          <strong>{event.title}</strong>
          {event.subtitle && <p>{event.subtitle}</p>}
        </div>
      </div>
      {(event.confirmation || event.address || event.details?.length) && (
        <div className="event-info">
          {event.confirmation && <p><b>Reserva</b><span>{event.confirmation}</span></p>}
          {event.address && <p><b>Dirección</b><span>{event.address}</span></p>}
          {event.details?.map((detail) => <p key={detail}><span>• {detail}</span></p>)}
        </div>
      )}
      <PassengerDetails event={event} />
      {event.status === 'review' && <div className="review-banner">Requiere verificación antes del viaje.</div>}
      <div className="event-actions">
        {event.route && <button type="button" className="secondary-button route-button" onClick={() => onRoute(event.route!)}><AppIcon name="directions" size={17} />Ver ruta</button>}
        {event.ticketEventId && <button type="button" className="secondary-button" onClick={() => onOpenTicket(event.ticketEventId!)}>Abrir boletos</button>}
        <button type="button" className="text-button edit-event-button" onClick={() => onEdit(event.id)}>Editar</button>
      </div>
    </article>
  );
}

function TodayView({ days, onOpenTicket, onExpense, onRoute }: { days: TripDay[]; onOpenTicket: (eventId?: string) => void; onExpense: () => void; onRoute: (route: RouteRequest) => void }) {
  const day = operationalDay(days);
  const isActual = day.date === localIsoDate();
  const nextTicketEvent = day.events.find((event) => event.ticketEventId)?.ticketEventId;
  const nextRoute = day.events.find((event) => event.route)?.route;
  return (
    <section className="view">
      <div className="hero">
        <div>
          <p className="eyebrow">{isActual ? 'HOY' : 'PRÓXIMO DÍA DEL VIAJE'}</p>
          <h1>{day.city}</h1>
          <p>{labelDate(day.date)} · {day.country}</p>
        </div>
        <div className="offline-pill">Offline listo</div>
      </div>
      <div className="quick-actions">
        <button type="button" onClick={() => onOpenTicket(nextTicketEvent)}><AppIcon name="tickets" /><span>Boletos</span></button>
        <button type="button" disabled={!nextRoute} onClick={() => nextRoute && onRoute(nextRoute)}><AppIcon name="directions" /><span>Cómo llegar</span>{!nextRoute && <small>Sin ruta</small>}</button>
        <button type="button" disabled><AppIcon name="food" /><span>Comer</span><small>V0.4</small></button>
        <button type="button" className="expense-quick" onClick={onExpense}><AppIcon name="expenses" /><span>Gasto</span></button>
      </div>
      <div className="today-heading"><strong>{longDate(day.date)}</strong><span>{day.events.length} evento{day.events.length === 1 ? '' : 's'}</span></div>
      <div className="timeline">
        {day.events.map((event) => {
          const visual = eventVisual(event);
          return (
            <article className={`event ${event.status === 'review' ? 'review' : ''}`} key={event.id}>
              <div className="event-time"><AppIcon name={visual.icon} size={18} /><span>{event.time ?? '—'}</span></div>
              <div className="event-body">
                <strong>{event.title}</strong>
                {event.subtitle && <span>{event.subtitle}</span>}
                {event.confirmation && <small>Reserva {event.confirmation}</small>}
                {event.passengers?.some((passenger) => passenger.seat) && <small>{event.passengers.map((passenger) => passenger.seat ? `${passenger.name.split(' ')[0]} ${passenger.seat}` : passenger.name.split(' ')[0]).join(' · ')}</small>}
                {event.status === 'review' && <em>Requiere verificación</em>}
                <div className="inline-actions">{event.route && <button type="button" className="inline-button" onClick={() => onRoute(event.route!)}>Ver ruta</button>}{event.ticketEventId && <button type="button" className="inline-button" onClick={() => onOpenTicket(event.ticketEventId)}>Ver boletos</button>}</div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function TripView({ days, onOpenTicket, onRoute, onEdit, onOpenPlanner }: { days: TripDay[]; onOpenTicket: (eventId: string) => void; onRoute: (route: RouteRequest) => void; onEdit: (eventId: string) => void; onOpenPlanner: () => void }) {
  const today = localIsoDate();
  const orderedDays = useMemo(() => {
    const active = days.filter((day) => day.date >= today);
    const passed = days.filter((day) => day.date < today);
    return [...active, ...passed];
  }, [days, today]);

  return (
    <section className="view">
      <header className="section-head trip-head"><div><h2>Itinerario completo</h2><small>{days.length} días · los días realizados pasan al final</small></div><button type="button" className="planner-button" onClick={onOpenPlanner}>Editar viaje</button></header>
      <div className="day-list">
        {orderedDays.map((day) => {
          const isPast = day.date < today;
          return (
            <details key={day.date} className={`day-card ${isPast ? 'is-past-day' : ''}`}>
              <summary>
                <span className="date-box">{labelDate(day.date)}</span>
                <span><strong>{day.city}</strong><small>{day.country}</small></span>
                <b>{isPast ? 'Realizado' : day.events.length}</b>
              </summary>
              <div className="day-detail">
                {day.events.map((event) => <EventDetails key={event.id} event={event} onOpenTicket={onOpenTicket} onRoute={onRoute} onEdit={onEdit} />)}
              </div>
            </details>
          );
        })}
      </div>
    </section>
  );
}

function findEvent(days: TripDay[], eventId: string | null): { day: TripDay; event: TripEvent } | null {
  if (!eventId) return null;
  for (const day of days) {
    const event = day.events.find((item) => item.id === eventId);
    if (event) return { day, event };
  }
  return null;
}

function PlanningEditor({ days, state, initialEventId, onClose, onSave }: { days: TripDay[]; state: PlanningState; initialEventId: string | null; onClose: () => void; onSave: (next: PlanningState) => Promise<void> }) {
  const [mode, setMode] = useState<'edit' | 'add'>(initialEventId ? 'edit' : 'add');
  const [eventId, setEventId] = useState(initialEventId ?? days[0]?.events[0]?.id ?? '');
  const [date, setDate] = useState(days[0]?.date ?? localIsoDate());
  const [time, setTime] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [status, setStatus] = useState<'confirmed' | 'review'>('review');
  const [message, setMessage] = useState('');
  const selected = findEvent(days, eventId);

  useEffect(() => {
    if (mode !== 'edit' || !selected) return;
    setDate(selected.day.date);
    setTime(selected.event.time ?? '');
    setTitle(selected.event.title);
    setSubtitle(selected.event.subtitle ?? '');
    setStatus(selected.event.status ?? 'confirmed');
  }, [eventId, mode]);

  useEffect(() => {
    if (initialEventId) { setMode('edit'); setEventId(initialEventId); }
  }, [initialEventId]);

  function switchMode(next: 'edit' | 'add') {
    setMode(next); setMessage('');
    if (next === 'add') {
      setDate(days.find((day) => day.date >= localIsoDate())?.date ?? days[0]?.date ?? localIsoDate());
      setTime(''); setTitle(''); setSubtitle(''); setStatus('review');
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !date) { setMessage('Captura fecha y nombre de la actividad.'); return; }
    if (mode === 'edit' && selected) {
      const next: PlanningState = {
        ...state,
        overrides: { ...state.overrides, [selected.event.id]: { date, time, title: title.trim(), subtitle: subtitle.trim(), status } },
      };
      await onSave(next);
      setMessage('Cambio guardado en este dispositivo.');
      return;
    }
    const next: PlanningState = {
      ...state,
      additions: [...state.additions, { date, event: { id: `local-${crypto.randomUUID()}`, time: time || undefined, title: title.trim(), subtitle: subtitle.trim() || undefined, kind: 'note', status } }],
    };
    await onSave(next);
    setMessage('Actividad agregada en este dispositivo.');
    setTime(''); setTitle(''); setSubtitle(''); setStatus('review');
  }

  return (
    <div className="planning-overlay" role="dialog" aria-modal="true" aria-label="Editar itinerario" onClick={onClose}>
      <section className="planning-sheet" onClick={(event) => event.stopPropagation()}>
        <header><div><p className="eyebrow">PLANEACIÓN</p><h2>Editar viaje</h2></div><button type="button" className="overlay-close planning-close" onClick={onClose} aria-label="Cerrar"><AppIcon name="x" size={20} /></button></header>
        <div className="planning-tabs"><button type="button" className={mode === 'add' ? 'active' : ''} onClick={() => switchMode('add')}>Agregar actividad</button><button type="button" className={mode === 'edit' ? 'active' : ''} onClick={() => switchMode('edit')}>Editar hito</button></div>
        <form onSubmit={submit}>
          {mode === 'edit' && <label>Hito<select value={eventId} onChange={(event) => setEventId(event.target.value)}>{days.flatMap((day) => day.events.map((item) => <option key={item.id} value={item.id}>{labelDate(day.date)} · {item.time ?? '—'} · {item.title}</option>))}</select></label>}
          <div className="planning-date-row"><label>Fecha<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label><label>Hora<input type="time" value={time} onChange={(event) => setTime(event.target.value)} /></label></div>
          <label>Actividad<input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Nombre de la actividad" /></label>
          <label>Detalle<textarea value={subtitle} onChange={(event) => setSubtitle(event.target.value)} placeholder="Ubicación, indicación práctica o cambio" rows={3} /></label>
          <label>Estado<select value={status} onChange={(event) => setStatus(event.target.value as 'confirmed' | 'review')}><option value="confirmed">Confirmado</option><option value="review">Pendiente / revisar</option></select></label>
          <button className="save-button" type="submit">Guardar cambio</button>
          <p className="planning-local-note">Los cambios de planeación se guardan offline en este dispositivo. La sincronización familiar central se habilitará con un backend protegido; no se publicará un endpoint de escritura sin autenticación.</p>
          <p className="form-message" role="status">{message}</p>
        </form>
      </section>
    </div>
  );
}

function MapsView({ route, onClear }: { route: RouteRequest | null; onClear: () => void }) {
  const [packId, setPackId] = useState('barcelona');
  const appleMode = route?.mode === 'walking' ? 'w' : route?.mode === 'transit' ? 'r' : 'd';
  const googleMode = route?.mode === 'walking' ? 'walking' : route?.mode === 'transit' ? 'transit' : 'driving';
  const appleUrl = route ? `https://maps.apple.com/?saddr=${encodeURIComponent(route.origin)}&daddr=${encodeURIComponent(route.destination)}&dirflg=${appleMode}` : '';
  const googleUrl = route ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(route.origin)}&destination=${encodeURIComponent(route.destination)}&travelmode=${googleMode}` : '';

  return (
    <section className="view route-view">
      <header className="section-head"><div><h2>Mapas</h2><small>Mapas, lugares y rutas guardadas</small></div>{route && <button type="button" className="text-button" onClick={onClear}>Limpiar ruta externa</button>}</header>
      <nav className="map-city-picker" aria-label="Elegir ciudad del mapa">
        {offlineCityPacks.filter((pack) => pack.phase === 'map-ready').map((pack) => <button key={pack.id} type="button" className={pack.id === packId ? 'active' : ''} onClick={() => setPackId(pack.id)}>{pack.city}</button>)}
      </nav>
      <Suspense fallback={<div className="deferred-module-status" role="status">Preparando mapa…</div>}>
        <CityMap key={packId} packId={packId} />
      </Suspense>
      {route && (
        <article className="route-card">
          <div className="route-point"><span>A</span><div><small>Origen</small><strong>{route.originLabel ?? route.origin}</strong></div></div>
          <div className="route-line" />
          <div className="route-point"><span>B</span><div><small>Destino</small><strong>{route.destinationLabel ?? route.destination}</strong></div></div>
          <div className="route-mode"><AppIcon name="directions" size={18} /><span>{route.mode === 'walking' ? 'A pie' : route.mode === 'transit' ? 'Transporte público' : 'Automóvil / taxi'}</span></div>
          <div className="route-map-actions"><a className="save-button" href={appleUrl} target="_blank" rel="noreferrer">Abrir en Apple Maps</a><a className="secondary-button" href={googleUrl} target="_blank" rel="noreferrer">Abrir en Google Maps</a></div>
          <p className="route-note">La ruta externa sigue disponible cuando hay conexión; el mapa superior conserva tus lugares y rutas guardadas.</p>
        </article>
      )}
    </section>
  );
}

function WalletCode({ code, onExpand }: { code: WalletTicket; onExpand: () => void }) {
  return (
    <div className="wallet-code-card">
      <div className="wallet-code-label">
        <small>{code.secondary ?? (code.codeType === 'barcode' ? 'Código de acceso' : 'QR de acceso')}</small>
        <strong>{code.label}</strong>
        {code.note && <p>{code.note}</p>}
      </div>
      <button type="button" className="code-image-button" onClick={onExpand} aria-label="Ampliar código">
        <img src={`data:image/png;base64,${code.imageBase64}`} alt={`Código de ${code.label}`} />
      </button>
      <button type="button" className="secondary-button" onClick={onExpand}>Código grande</button>
    </div>
  );
}

function TicketsView({ requestedEvent, onRequestedHandled }: { requestedEvent: string | null; onRequestedHandled: () => void }) {
  const [wallet, setWallet] = useState<WalletPayload | null>(null);
  const [walletState, setWalletState] = useState<'loading' | 'locked' | 'ready'>('loading');
  const [passphrase, setPassphrase] = useState('');
  const [message, setMessage] = useState('');
  const [selectedEvent, setSelectedEvent] = useState<string | null>(requestedEvent);
  const [selectedCode, setSelectedCode] = useState(0);
  const [expanded, setExpanded] = useState<WalletTicket | null>(null);

  useEffect(() => {
    getWalletKey().then(async (key) => {
      if (!key) { setWalletState('locked'); return; }
      try {
        setWallet(await decryptWalletWithStoredKey(key));
        setWalletState('ready');
      } catch {
        await clearWalletKey();
        setWalletState('locked');
      }
    }).catch(() => setWalletState('locked'));
  }, []);

  useEffect(() => {
    if (requestedEvent) {
      setSelectedEvent(requestedEvent);
      setSelectedCode(0);
      onRequestedHandled();
    }
  }, [requestedEvent, onRequestedHandled]);

  async function unlock(event: FormEvent) {
    event.preventDefault();
    if (!passphrase.trim()) return;
    setMessage('Abriendo paquete seguro…');
    try {
      const result = await decryptWalletWithPassphrase(passphrase);
      await setWalletKey(result.keyBase64);
      setWallet(result.payload);
      setWalletState('ready');
      setPassphrase('');
      setMessage('Wallet habilitado en este dispositivo.');
    } catch {
      setMessage('Clave incorrecta o paquete no disponible.');
    }
  }

  async function lockWallet() {
    await clearWalletKey();
    setWallet(null);
    setWalletState('locked');
    setSelectedEvent(null);
    setMessage('Wallet bloqueado en este dispositivo.');
  }

  const selectedMeta = selectedEvent ? ticketMetaByEvent[selectedEvent] : null;
  const codes = selectedEvent && wallet ? wallet.tickets.filter((item) => item.eventId === selectedEvent) : [];
  const currentCode = codes[selectedCode] ?? codes[0];

  return (
    <section className="view">
      <header className="section-head wallet-head">
        <div><h2>Boletos</h2><small>{walletState === 'ready' ? 'Wallet offline activo' : 'Wallet seguro'}</small></div>
        <div className="wallet-head-actions">
          {selectedMeta && (
            <button type="button" className="wallet-back-button" onClick={() => { setSelectedEvent(null); setSelectedCode(0); }} aria-label="Regresar a Wallet" title="Regresar a Wallet">
              <AppIcon name="back" size={23} />
            </button>
          )}
          {walletState === 'ready' && (
            <button type="button" className="wallet-lock-button" onClick={lockWallet} aria-label="Bloquear Wallet" title="Bloquear Wallet">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lock-keyhole" aria-hidden="true">
                <circle cx="12" cy="16" r="1" />
                <rect x="3" y="10" width="18" height="12" rx="2" />
                <path d="M7 10V7a5 5 0 0 1 10 0v3" />
              </svg>
            </button>
          )}
        </div>
      </header>

      {walletState === 'loading' && <div className="empty-state"><strong>Preparando Wallet…</strong></div>}
      {walletState === 'locked' && (
        <form className="wallet-unlock" onSubmit={unlock}>
          <div className="secure-mark"><AppIcon name="lock" size={30} /></div>
          <strong>Activar Wallet offline</strong>
          <p>Los códigos de acceso están cifrados. Introduce la clave del viaje una sola vez; este dispositivo la recordará localmente.</p>
          <input type="password" autoCapitalize="none" autoCorrect="off" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} placeholder="Clave del Wallet" aria-label="Clave del Wallet" />
          <button className="save-button" type="submit">Desbloquear</button>
          <small>El paquete cifrado queda disponible offline; la clave no forma parte del sitio público.</small>
          <p className="form-message" role="status">{message}</p>
        </form>
      )}

      {selectedMeta && (
        <article className={`ticket-focus ${selectedMeta.warning ? 'review' : ''}`}>
          <small>{labelDate(selectedMeta.date)} · {selectedMeta.city}</small>
          <h3>{selectedMeta.title}</h3>
          <p className="ticket-time">{selectedMeta.time} · {selectedMeta.access}</p>
          {selectedMeta.booking && <p className="booking-line"><b>Reserva</b> {selectedMeta.booking}</p>}
          {selectedMeta.warning && <div className="review-banner">{selectedMeta.warning}</div>}
          <div className="requirements">{selectedMeta.requirements.map((item) => <span key={item}>• {item}</span>)}</div>

          {walletState === 'ready' && codes.length > 0 && currentCode && (
            <>
              <div className="code-selector" aria-label="Seleccionar boleto">
                {codes.map((code, index) => (
                  <button type="button" key={code.id} className={selectedCode === index ? 'active' : ''} onClick={() => setSelectedCode(index)}>{index + 1}</button>
                ))}
              </div>
              <div className="code-count">Boleto {Math.min(selectedCode + 1, codes.length)} de {codes.length}</div>
              <WalletCode code={currentCode} onExpand={() => setExpanded(currentCode)} />
            </>
          )}
          {walletState === 'ready' && codes.length === 0 && <div className="empty-state compact"><strong>Código no disponible</strong><p>Conservamos los metadatos, pero este evento aún no tiene imagen de acceso en el paquete.</p></div>}
          {walletState !== 'ready' && <p className="locked-hint">Desbloquea el Wallet arriba para mostrar los códigos de acceso.</p>}
        </article>
      )}

      {!selectedMeta && (
        <div className="ticket-grid">
          {ticketMeta.map((meta) => (
            <article className={`ticket-card ${meta.warning ? 'review' : ''}`} key={meta.eventId}>
              <small>{labelDate(meta.date)} · {meta.city}</small>
              <strong>{meta.title}</strong>
              <span>{meta.time} · {meta.access}</span>
              {meta.warning ? <p className="warning-copy">{meta.warning}</p> : <p>{meta.count} boletos · preparados para acceso offline</p>}
              <button type="button" className="secondary-button" onClick={() => { setSelectedEvent(meta.eventId); setSelectedCode(0); }}>Abrir Wallet</button>
            </article>
          ))}
        </div>
      )}

      {expanded && (
        <div className="code-overlay" role="dialog" aria-modal="true" aria-label="Código de acceso ampliado" onClick={() => setExpanded(null)}>
          <button type="button" className="overlay-close" onClick={() => setExpanded(null)} aria-label="Cerrar"><AppIcon name="x" size={22} /></button>
          <div className="overlay-ticket" onClick={(e) => e.stopPropagation()}>
            <small>{expanded.secondary ?? 'Acceso'}</small>
            <strong>{expanded.label}</strong>
            <img src={`data:image/png;base64,${expanded.imageBase64}`} alt={`Código ampliado de ${expanded.label}`} />
            <p>Mantén esta pantalla abierta al llegar al control.</p>
          </div>
        </div>
      )}
    </section>
  );
}

function BackToMoreButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="section-back-button" onClick={onClick}>
      <AppIcon name="back" size={23} />
      <span>Regresar</span>
    </button>
  );
}

function PlaceholderView({ title, text }: { title: string; text: string }) {
  return <section className="view"><header className="section-head"><h2>{title}</h2></header><div className="empty-state"><strong>Base preparada</strong><p>{text}</p></div></section>;
}

function ExpensesView({ onBack }: { onBack?: () => void }) {
  const [owner, setOwner] = useState<ExpenseOwner | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<ExpenseCurrency>('EUR');
  const [category, setCategory] = useState('food');
  const [scope, setScope] = useState<'group' | 'all'>('group');
  const [date, setDate] = useState(localIsoDate());
  const [note, setNote] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([getExpenseOwner(), listExpenses()]).then(([savedOwner, savedExpenses]) => {
      setOwner(savedOwner);
      setExpenses(savedExpenses);
    }).catch(() => setMessage('No fue posible abrir el almacenamiento local.'));
  }, []);

  const ownerExpenses = useMemo(
    () => expenses.filter((expense) => expense.owner === owner),
    [expenses, owner],
  );

  const totals = useMemo(() => {
    const byCurrency: Partial<Record<ExpenseCurrency, number>> = {};
    ownerExpenses.forEach((expense) => {
      byCurrency[expense.currency] = (byCurrency[expense.currency] ?? 0) + expense.amount;
    });
    return byCurrency;
  }, [ownerExpenses]);

  const categoryTotals = useMemo(() => {
    const summary = new Map<string, Partial<Record<ExpenseCurrency, number>>>();
    ownerExpenses.forEach((expense) => {
      const current = summary.get(expense.category) ?? {};
      current[expense.currency] = (current[expense.currency] ?? 0) + expense.amount;
      summary.set(expense.category, current);
    });
    return [...summary.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ownerExpenses]);

  const personTotals = useMemo(() => {
    const summary = new Map<TravelerId, Partial<Record<ExpenseCurrency, number>>>();
    ownerExpenses.forEach((expense) => {
      const share = expense.amount / expense.beneficiaries.length;
      expense.beneficiaries.forEach((person) => {
        const current = summary.get(person) ?? {};
        current[expense.currency] = (current[expense.currency] ?? 0) + share;
        summary.set(person, current);
      });
    });
    return [...summary.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [ownerExpenses]);

  function resetForm() {
    setAmount('');
    setCurrency('EUR');
    setCategory('food');
    setScope('group');
    setDate(localIsoDate());
    setNote('');
    setEditingId(null);
  }

  async function chooseOwner(value: ExpenseOwner) {
    await setExpenseOwner(value);
    setOwner(value);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!owner) return;
    const numericAmount = Number(amount.replace(',', '.'));
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setMessage('Captura un importe válido.');
      return;
    }
    const previous = editingId ? expenses.find((expense) => expense.id === editingId) : undefined;
    const beneficiaries: TravelerId[] = scope === 'all'
      ? ['ricardo', 'aurora', 'arturo', 'ileana', 'arturo-jr']
      : ownerGroups[owner];
    const expense: Expense = {
      id: previous?.id ?? crypto.randomUUID(),
      createdAt: previous?.createdAt ?? new Date().toISOString(),
      date,
      owner,
      amount: Math.round(numericAmount * 100) / 100,
      currency,
      category,
      beneficiaries,
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    await saveExpense(expense);
    setExpenses((current) => {
      const withoutCurrent = current.filter((item) => item.id !== expense.id);
      return [expense, ...withoutCurrent].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    });
    resetForm();
    setMessage(previous ? 'Gasto actualizado offline.' : 'Gasto guardado offline.');
  }

  function editExpense(expense: Expense) {
    setEditingId(expense.id);
    setAmount(expense.amount.toFixed(2));
    setCurrency(expense.currency);
    setCategory(expense.category);
    setScope(expense.beneficiaries.length === 5 ? 'all' : 'group');
    setDate(expense.date);
    setNote(expense.note ?? '');
    setMessage('Editando gasto. Guarda para aplicar los cambios.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function removeExpense(expense: Expense) {
    if (!window.confirm('¿Eliminar este gasto? Esta acción sólo afecta este dispositivo.')) return;
    await deleteExpense(expense.id);
    setExpenses((current) => current.filter((item) => item.id !== expense.id));
    if (editingId === expense.id) resetForm();
    setMessage('Gasto eliminado.');
  }

  const travelerNames: Record<TravelerId, string> = {
    ricardo: 'Ricardo',
    aurora: 'Aurora',
    arturo: 'Arturo',
    ileana: 'Ileana',
    'arturo-jr': 'Arturo Jr.',
  };

  if (!owner) {
    return (
      <section className="view owner-select">
        {onBack && <BackToMoreButton onClick={onBack} />}
        <p className="eyebrow">CONTROL DE GASTOS</p>
        <h2>¿Quién está usando este dispositivo?</h2>
        <p>La selección quedará recordada localmente.</p>
        <button type="button" className="owner-choice" onClick={() => chooseOwner('ricardo')}>
          <strong>Ricardo Reig</strong><span>Ricardo + Aurora</span>
        </button>
        <button type="button" className="owner-choice" onClick={() => chooseOwner('arturo')}>
          <strong>Arturo Reig</strong><span>Arturo + Ileana + Arturo Jr.</span>
        </button>
      </section>
    );
  }

  return (
    <section className="view expense-view">
      {onBack && <BackToMoreButton onClick={onBack} />}
      <header className="section-head expense-head">
        <div>
          <p className="eyebrow">CONTROL DE GASTOS</p>
          <h2>Gastos · {owner === 'ricardo' ? 'Ricardo' : 'Arturo'}</h2>
          <button className="text-button" type="button" onClick={() => { resetForm(); setOwner(null); }}>Cambiar usuario</button>
        </div>
        <div className="totals" aria-label="Totales por divisa">
          {Object.entries(totals).length
            ? Object.entries(totals).map(([code, total]) => <span key={code}>{code} {total.toFixed(2)}</span>)
            : <span>Sin gastos</span>}
        </div>
      </header>

      <form className="expense-form" onSubmit={submit}>
        <div className="expense-form-heading">
          <strong>{editingId ? 'Editar gasto' : 'Registrar gasto'}</strong>
          {editingId && <button type="button" className="text-button" onClick={() => { resetForm(); setMessage('Edición cancelada.'); }}>Cancelar edición</button>}
        </div>
        <div className="amount-row">
          <select value={currency} onChange={(event) => setCurrency(event.target.value as ExpenseCurrency)} aria-label="Moneda">
            <option>EUR</option><option>GBP</option><option>CZK</option><option>MXN</option>
          </select>
          <input inputMode="decimal" placeholder="0.00" value={amount} onChange={(event) => setAmount(event.target.value)} aria-label="Importe" />
        </div>
        <div className="category-grid" aria-label="Categoría">
          {categories.map(([id, icon, label]) => (
            <button type="button" className={category === id ? 'selected' : ''} key={id} onClick={() => setCategory(id)}>
              <span><AppIcon name={icon} size={22} /></span><small>{label}</small>
            </button>
          ))}
        </div>
        <div className="expense-options">
          <label>Fecha<input type="date" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          <label>Beneficiarios<select value={scope} onChange={(event) => setScope(event.target.value as 'group' | 'all')}><option value="group">Mi grupo</option><option value="all">Todos</option></select></label>
        </div>
        <label className="expense-note">Nota opcional<input value={note} maxLength={120} placeholder="Ej. cena, taxi, entradas" onChange={(event) => setNote(event.target.value)} /></label>
        <button className="save-button" type="submit">{editingId ? 'Actualizar gasto' : 'Guardar gasto'}</button>
        <p className="form-message" role="status">{message}</p>
      </form>

      <section className="expense-summary" aria-label="Resumen de gastos">
        <div>
          <h3>Por persona</h3>
          {personTotals.length ? personTotals.map(([person, byCurrency]) => (
            <p key={person}><span>{travelerNames[person]}</span><b>{Object.entries(byCurrency).map(([code, total]) => code + ' ' + total.toFixed(2)).join(' · ')}</b></p>
          )) : <p className="expense-empty">Aún no hay importes repartidos.</p>}
        </div>
        <div>
          <h3>Por categoría</h3>
          {categoryTotals.length ? categoryTotals.map(([id, byCurrency]) => {
            const categoryLabel = categories.find(([categoryId]) => categoryId === id)?.[2] ?? id;
            return <p key={id}><span>{categoryLabel}</span><b>{Object.entries(byCurrency).map(([code, total]) => code + ' ' + total.toFixed(2)).join(' · ')}</b></p>;
          }) : <p className="expense-empty">Aún no hay categorías registradas.</p>}
        </div>
      </section>

      <section className="expense-history" aria-label="Historial de gastos">
        <div className="expense-history-heading"><h3>Historial</h3><span>{ownerExpenses.length} {ownerExpenses.length === 1 ? 'gasto' : 'gastos'}</span></div>
        {ownerExpenses.length ? <div className="expense-list">{ownerExpenses.map((expense) => {
          const categoryEntry = categories.find(([id]) => id === expense.category);
          return (
            <article key={expense.id}>
              <span><AppIcon name={categoryEntry?.[1] ?? 'plus'} size={20} /></span>
              <div>
                <strong>{categoryEntry?.[2] ?? expense.category}</strong>
                <small>{labelDate(expense.date)} · {expense.beneficiaries.map((person) => travelerNames[person]).join(', ')}</small>
                {expense.note && <small>{expense.note}</small>}
              </div>
              <b>{expense.currency} {expense.amount.toFixed(2)}</b>
              <div className="expense-row-actions">
                <button type="button" onClick={() => editExpense(expense)}>Editar</button>
                <button type="button" onClick={() => removeExpense(expense)}>Eliminar</button>
              </div>
            </article>
          );
        })}</div> : <div className="empty-state compact"><strong>Sin gastos aún</strong><p>Registra el primer gasto para verlo aquí y repartirlo por persona.</p></div>}
      </section>
    </section>
  );
}
function DiscoverView({ days, onBack }: { days: TripDay[]; onBack: () => void }) {
  const contextualDays = useMemo(() => days.filter((day) => recommendationsForDay(day).length > 0), [days]);
  const initialDate = contextualDays.find((day) => day.date >= localIsoDate())?.date ?? contextualDays[0]?.date ?? days[0]?.date ?? '';
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [filter, setFilter] = useState<DiscoverCategory | 'all'>('all');
  const selectedDay = contextualDays.find((day) => day.date === selectedDate) ?? contextualDays[0];
  const recommendations = selectedDay ? recommendationsForDay(selectedDay).filter((item) => filter === 'all' || item.category === filter) : [];

  return (
    <section className="view discover-view">
      <BackToMoreButton onClick={onBack} />
      <header className="section-head discover-head">
        <p className="eyebrow">GUÍA DEL VIAJE</p>
        <h2>Descubrir</h2>
        <p>Ideas reales preparadas según ciudad, fecha y los compromisos ya guardados.</p>
      </header>
      <div className="discover-days" role="tablist" aria-label="Días con recomendaciones">
        {contextualDays.map((day) => (
          <button key={day.date} type="button" role="tab" aria-selected={day.date === selectedDay?.date} className={day.date === selectedDay?.date ? 'active' : ''} onClick={() => { setSelectedDate(day.date); setFilter('all'); }}>
            <strong>{labelDate(day.date)}</strong><small>{day.city}</small>
          </button>
        ))}
      </div>
      {selectedDay && <>
        <section className="discover-itinerary" aria-label={`Agenda de ${longDate(selectedDay.date)}`}>
          <div><span>{longDate(selectedDay.date)}</span><strong>{selectedDay.city}</strong></div>
          <ul>{selectedDay.events.map((event) => <li key={event.id}><b>{event.time ?? 'Plan flexible'}</b><span>{event.title}</span></li>)}</ul>
        </section>
        <div className="discover-filters" aria-label="Filtrar recomendaciones">
          <button type="button" className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Todo</button>
          {(Object.keys(discoverCategoryLabels) as DiscoverCategory[]).map((category) => <button key={category} type="button" className={filter === category ? 'active' : ''} onClick={() => setFilter(category)}>{discoverCategoryLabels[category]}</button>)}
        </div>
        <div className="discover-list">
          {recommendations.map((recommendation) => (
            <article className="discover-card" key={recommendation.id}>
              <span className={`discover-tag ${recommendation.category}`}>{discoverCategoryLabels[recommendation.category]}</span>
              <h3>{recommendation.title}</h3>
              <p>{recommendation.description}</p>
              <div className="discover-context"><strong>Encaja hoy</strong><span>{recommendation.context}</span></div>
              <small>{recommendation.practical}</small>
              {recommendation.sourceUrl.startsWith('#') ? <span className="discover-source">Basado en los datos guardados del viaje</span> : <a className="discover-source" href={recommendation.sourceUrl} target="_blank" rel="noreferrer">Abrir {recommendation.sourceLabel}<AppIcon name="chevronRight" size={16} /></a>}
            </article>
          ))}
          {!recommendations.length && <div className="empty-state compact"><strong>No hay propuestas en este filtro</strong><p>Cambia la categoría para ver las opciones que encajan en este día.</p></div>}
        </div>
      </>}
    </section>
  );
}

function MoreView({ section, setSection, days }: { section: MoreSection; setSection: (section: MoreSection) => void; days: TripDay[] }) {
  if (section === 'expenses') return <ExpensesView onBack={() => setSection('menu')} />;
  if (section === 'discover') return <DiscoverView days={days} onBack={() => setSection('menu')} />;
  return (
    <section className="view">
      <header className="section-head"><h2>Más</h2><span>Europa 2026 · V{APP_VERSION}</span></header>
      <div className="more-grid">
        <button type="button" onClick={() => setSection('expenses')}><span><AppIcon name="expenses" /></span><div><strong>Gastos</strong><small>Registro offline y control por grupo</small></div><b><AppIcon name="chevronRight" size={20} /></b></button>
        <button type="button" onClick={() => setSection('discover')}><span><AppIcon name="discover" /></span><div><strong>Descubrir</strong><small>Recomendaciones por ciudad y agenda</small></div><b><AppIcon name="chevronRight" size={20} /></b></button>
        <button type="button" disabled><span><AppIcon name="assistant" /></span><div><strong>Asistente de viaje</strong><small>Próximamente</small></div><b><AppIcon name="chevronRight" size={20} /></b></button>
        <button type="button" disabled><span><AppIcon name="offline" /></span><div><strong>Estado offline</strong><small>Itinerario y Wallet preparados</small></div><b><AppIcon name="chevronRight" size={20} /></b></button>
      </div>
    </section>
  );
}

export default function App() {
  const [tab, setTab] = useState<Tab>('today');
  const [online, setOnline] = useState(navigator.onLine);
  const [moreSection, setMoreSection] = useState<MoreSection>('menu');
  const [requestedTicket, setRequestedTicket] = useState<string | null>(null);
  const [mapRoute, setMapRoute] = useState<RouteRequest | null>(null);
  const [planningState, setPlanningState] = useState<PlanningState>(emptyPlanningState);
  const [tripDays, setTripDays] = useState<TripDay[]>(itinerary);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [plannerEventId, setPlannerEventId] = useState<string | null>(null);

  useEffect(() => {
    const on = () => setOnline(true); const off = () => setOnline(false);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  useEffect(() => {
    loadPlanningState().then((saved) => {
      setPlanningState(saved);
      setTripDays(applyPlanningState(itinerary, saved));
    }).catch(() => {
      setPlanningState(emptyPlanningState);
      setTripDays(itinerary);
    });
  }, []);

  async function persistPlanning(next: PlanningState) {
    await savePlanningState(next);
    setPlanningState(next);
    setTripDays(applyPlanningState(itinerary, next));
  }

  function openTicket(eventId?: string) { setRequestedTicket(eventId ?? null); setTab('tickets'); }
  function openExpenses() { setMoreSection('expenses'); setTab('more'); }
  function openRoute(route: RouteRequest) { setMapRoute(route); setTab('maps'); }
  function openPlanner(eventId: string | null = null) { setPlannerEventId(eventId); setPlannerOpen(true); }
  function changeTab(next: Tab) { setTab(next); if (next === 'more') setMoreSection('menu'); }

  return (
    <div className="app-shell">
      <header className="topbar"><div><strong>EUROPA</strong><span>2026</span></div><small className={online ? 'online' : 'offline'}>{online ? 'Conectado' : 'Sin conexión'}</small></header>
      <main>
        {tab === 'today' && <TodayView days={tripDays} onOpenTicket={openTicket} onExpense={openExpenses} onRoute={openRoute} />}
        {tab === 'trip' && <TripView days={tripDays} onOpenTicket={(id) => openTicket(id)} onRoute={openRoute} onEdit={(id) => openPlanner(id)} onOpenPlanner={() => openPlanner()} />}
        {tab === 'tickets' && <TicketsView requestedEvent={requestedTicket} onRequestedHandled={() => setRequestedTicket(null)} />}
        {tab === 'maps' && <MapsView route={mapRoute} onClear={() => setMapRoute(null)} />}
        {tab === 'more' && <MoreView section={moreSection} setSection={setMoreSection} days={tripDays} />}
      </main>
      <nav className="bottom-nav" aria-label="Navegación principal">{tabs.map(([id, label, icon]) => <button key={id} type="button" className={tab === id ? 'active' : ''} onClick={() => changeTab(id)}><span><AppIcon name={icon} size={22} /></span><small>{label}</small></button>)}</nav>
      {plannerOpen && <PlanningEditor days={tripDays} state={planningState} initialEventId={plannerEventId} onClose={() => { setPlannerOpen(false); setPlannerEventId(null); }} onSave={persistPlanning} />}
    </div>
  );
}
